import { Button } from "shared/ui/shadcn/button";
import { PlusIcon } from "lucide-react";
import { useEmailTemplateSequencing } from "@/hooks/use-email-templates-manager/use-email-template-followup";
import { EmailPreviewCard } from "./email-preview-card";
import { useEmailTemplatesContext } from "@/components/modules/email-templates-sidebar/context";
import { FC, Fragment, useEffect, useMemo, useRef } from "react";
import striptags from "striptags";
import { clsxMerge } from "shared/lib/helpers";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import {
  useAddEmailToSequence,
  useCreateEmailSequence,
  useFetchEmailSequence,
  useFetchEmailSequenceById,
} from "@/components/modules/email-templates-sidebar/queries";
import { toast } from "react-hot-toast";
import { useAccountDetailsContext } from "@/components/modules/pipeline/account-details/context";
import {
  EmailDraftI,
  EmailSequenceItemI,
} from "@/components/modules/email-templates-sidebar/interfaces";
import { asyncGet, asyncWaitFor } from "@/helpers/context";
import { useHandlerRemoveEmailFromSequence } from "@/modules/email-templates-sidebar/hooks";

export const FollowupPreviewSection: FC<
  PropsWithClassNameI & { isHiddenActions?: boolean }
> = ({ className, isHiddenActions }) => {
  const {
    campaign,
    account,
    focusedContact: contact,
  } = useAccountDetailsContext();

  const {
    drafts,
    activeTemplateId,
    activeEmailSequenceItemId,
    handleComposeWithAI,
    setActiveEmailSequenceItemId,
    setDrafts,
    setDraft,
    getDraft,
    setEmailData,
    isAIGenerationStarted,
    isAIGenerationComplete,
  } = useEmailTemplatesContext();
  const { sequenceId, emailSequence, setEmailSequence, setSequenceId } =
    useEmailTemplateSequencing(
      drafts?.[activeTemplateId as string]?.sequence_id,
      drafts?.[activeTemplateId as string]?.emails
    );

  const isEmailGenerationInProgress = useMemo(
    () => isAIGenerationStarted && !isAIGenerationComplete,
    [isAIGenerationStarted, isAIGenerationComplete]
  );

  const {
    mutateAsync: createEmailSequenceAsync,
    isPending: isLoadingCreateEmailSequence,
  } = useCreateEmailSequence();
  const {
    mutateAsync: addEmailToSequenceAsync,
    isPending: isLoadingAddEmailToSequence,
  } = useAddEmailToSequence();

  const { data: emailSequenceData, refetch: refetchEmailSequence } =
    useFetchEmailSequence(sequenceId as string);
  const { mutateAsync: fetchEmailSequenceByIdAsync } =
    useFetchEmailSequenceById();

  const { handler: handlerRemoveEmailFromSequence } =
    useHandlerRemoveEmailFromSequence();

  useEffect(() => {
    if (sequenceId) refetchEmailSequence();
  }, [sequenceId]);

  /**
   * NOTE
   * Creating emails sequence creates a draft for the first email
   * Then we need to add the rest of the emails to the sequence
   *
   * Also when creating a sequence we need to make sure that
   * we save current draft subject and body to newly created sequence 0 item
   */
  const createEmailSequence = async () => {
    const resp = await createEmailSequenceAsync({
      campaign_id: campaign?.id as string,
      account_id: account?.id as string,
      contact_id: contact?.id as string,
    });

    if (resp.status !== 200) return;

    setSequenceId(resp.data.sequence_id);
    setActiveEmailSequenceItemId(resp.data?.email_id);

    const { data: emailSequenceData } = await fetchEmailSequenceByIdAsync(
      resp.data.sequence_id
    );

    /**
     * Update the first draft with form values
     * that have been created when there was no sequence
     */
    const draft = getDraft(activeTemplateId as string);
    setDraft(activeTemplateId as string, {
      ...draft,
      sequence_id: resp.data.sequence_id,
      emails: emailSequenceData.emails?.map((email, i) => {
        if (i !== 0) return email;

        return {
          ...email,
          subject: draft?.subject,
          body: draft?.body,
          attachments: draft?.attachments,
        };
      }),
    });

    /**
     * Add second email to sequence
     */
    await addEmailToSequence();
  };

  const handleComposeWithAIRef = useRef(handleComposeWithAI);

  useEffect(() => {
    handleComposeWithAIRef.current = handleComposeWithAI;
  }, [handleComposeWithAI]);

  const addEmailToSequence = async () => {
    const sequenceId = await asyncGet(setSequenceId);

    if (!sequenceId) {
      toast.error("Failed to add followup as sequence is not found.");
      return;
    }

    const resp = await addEmailToSequenceAsync(sequenceId as string);

    if (resp.status !== 200) {
      toast.error("Failed to add followup to sequence.");
      return;
    }

    const emailId = resp.data?.email_id;

    setActiveEmailSequenceItemId(emailId);

    /**
     * Wait until the draft is updated
     * so we can start composing with AI
     */
    await asyncWaitFor(setDrafts, {
      expectedValue: (drafts: Record<string, EmailDraftI>) => {
        const draft = drafts?.[activeTemplateId as string];
        const emails = draft?.emails;
        const email = emails?.find((email) => email.email_id === emailId);
        return !!email;
      },
    });

    //Removed auto email generation
    // handleComposeWithAIRef?.current?.();
  };

  const handleAddFollowup = async () => {
    if (!sequenceId) {
      await createEmailSequence();

      return;
    }

    await addEmailToSequence();
  };

  const handleSelectPreview = (emailId: string) => () => {
    setActiveEmailSequenceItemId(emailId);
  };

  const handleRemoveEmailFromSequence = (emailId: string) => async () => {
    handlerRemoveEmailFromSequence(emailId);
  };

  const isLoading = useMemo(
    () => isLoadingCreateEmailSequence || isLoadingAddEmailToSequence,
    [isLoadingCreateEmailSequence, isLoadingAddEmailToSequence]
  );

  const emailSubject = useMemo(
    () => drafts?.[activeTemplateId as string]?.subject,
    [drafts, activeTemplateId]
  );

  const emailBody = useMemo(
    () => drafts?.[activeTemplateId as string]?.body,
    [drafts, activeTemplateId]
  );

  const sequenceDraftsMap = useMemo(() => {
    const emails = drafts?.[activeTemplateId as string]?.emails;
    if (!emails) return {};

    return emails.reduce(
      (emailsMap, email) => {
        emailsMap[email.email_id] = email;
        return emailsMap;
      },
      {} as Record<string, EmailSequenceItemI>
    );
  }, [drafts, activeTemplateId]);

  const isAvailableAddButton = useMemo(() => {
    return (
      !isHiddenActions &&
      (drafts?.[activeTemplateId as string]?.emails?.length || 0) <= 6
    );
  }, [drafts, activeTemplateId, isHiddenActions]);

  /**
   * Update the draft with the email sequence
   * So the form has fields where to edit the email
   */
  useEffect(() => {
    if (activeTemplateId && typeof activeTemplateId === "string") {
      const draft = getDraft(activeTemplateId);

      setDraft(activeTemplateId, {
        ...draft,
        sequence_id: sequenceId,
        emails: emailSequence?.map((email) => {
          return {
            ...email,
            subject: sequenceDraftsMap[email.email_id]?.subject,
            body: sequenceDraftsMap[email.email_id]?.body,
            scheduled_at: sequenceDraftsMap[email.email_id]?.scheduled_at,
            attachments: sequenceDraftsMap[email.email_id]?.attachments,
          };
        }),
      });
    }
  }, [emailSequence]);

  useEffect(() => {
    setEmailSequence(emailSequenceData?.data?.emails);
  }, [emailSequenceData]);

  useEffect(() => {
    if (activeTemplateId && typeof activeTemplateId === "string") {
      const draft = getDraft(activeTemplateId);
      setEmailData(draft);
      setEmailSequence(draft?.emails);
      setSequenceId(draft?.sequence_id);

      /**
       * Set the first email as active
       * when we switch between templates
       * we select either first email in the sequence or none at all
       */
      setActiveEmailSequenceItemId(draft?.emails?.[0]?.email_id || undefined);

      setEmailSequence(draft?.emails);
    }
  }, [activeTemplateId]);

  return (
    <section
      className={clsxMerge(
        "flex w-full max-w-[500px] flex-col gap-4 pl-4",
        className
      )}
    >
      {!emailSequence?.length && (
        <EmailPreviewCard
          index={0}
          isSelected={!activeEmailSequenceItemId}
          subject={emailSubject}
          body={striptags(emailBody || "")}
          isDisabled={isEmailGenerationInProgress}
        />
      )}

      {emailSequence?.map((email, index) => (
        <Fragment key={email.email_id}>
          {index === 0 && (
            <EmailPreviewCard
              index={0}
              isSelected={email.email_id === activeEmailSequenceItemId}
              subject={sequenceDraftsMap[email.email_id]?.subject}
              body={striptags(sequenceDraftsMap[email.email_id]?.body || "")}
              isDisabled={isEmailGenerationInProgress}
              onClick={handleSelectPreview(email.email_id)}
            />
          )}

          {index !== 0 && (
            <EmailPreviewCard
              index={index}
              isSelected={email.email_id === activeEmailSequenceItemId}
              subject={sequenceDraftsMap[email.email_id]?.subject}
              body={striptags(sequenceDraftsMap[email.email_id]?.body || "")}
              scheduledAt={sequenceDraftsMap[email.email_id]?.scheduled_at}
              prevScheduledAt={
                index > 1
                  ? drafts?.[activeTemplateId as string]?.emails?.[index - 1]
                      ?.scheduled_at
                  : undefined
              }
              isDisabled={isEmailGenerationInProgress}
              onClick={handleSelectPreview(email.email_id)}
              onRemove={handleRemoveEmailFromSequence(email.email_id)}
            />
          )}
        </Fragment>
      ))}

      {isAvailableAddButton && (
        <Button
          variant="outline"
          onClick={handleAddFollowup}
          disabled={isLoading || isEmailGenerationInProgress}
          isLoading={isLoading}
        >
          <PlusIcon className="mr-1 h-4 w-4" />
          Add Follow-up
        </Button>
      )}
    </section>
  );
};
